import { useEffect, useMemo, useState, useCallback } from 'react'
import {
    MailerTemplateVariableItem,
    MailerReceiver,
    MailerCampaignSharedVariable,
    SYSTEM_VARIABLES,
} from '../../../types'

// TemplateVariable type from TextEditorCustomize
interface TemplateVariable {
    name: string
    label: string
    description?: string
    color?: string
}

interface UseVariableManagementOptions {
    // Full variable objects for customer/receiver variables
    initialCustomerVariables?: MailerTemplateVariableItem[]
    // Full shared variable objects with values
    initialSharedVariables?: MailerCampaignSharedVariable[]
    // Always show receiver variables even if not in content (for internal filters mode)
    alwaysShowReceiverVariables?: boolean
}

export const useVariableManagement = (
    templateContent: string,
    selectedTemplateVariables: MailerTemplateVariableItem[],
    options?: UseVariableManagementOptions
) => {
    // Store names for quick lookup (derived from full objects)
    const [customerVariableNames, setCustomerVariableNames] = useState<
        string[]
    >(options?.initialCustomerVariables?.map((v) => v.name) || [])
    const [draggedVariable, setDraggedVariable] = useState<string | null>(null)
    // Store shared variable values (name -> value) for easy lookup
    const [sharedVariableValues, setSharedVariableValues] = useState<
        Record<string, string>
    >(() => {
        // Initialize from full shared variable objects
        const values: Record<string, string> = {}
        options?.initialSharedVariables?.forEach((v) => {
            if (v.value !== undefined) {
                values[v.name] = v.value
            }
        })
        return values
    })
    const [showPreview, setShowPreview] = useState(false)
    const [previewReceiverIndex, setPreviewReceiverIndex] = useState(0)

    // Track all variables from TextEditor (including custom ones)
    const [trackedVariables, setTrackedVariables] = useState<
        TemplateVariable[]
    >([])

    // Sync with initial values when they change (e.g., when campaign loads)
    // Use JSON.stringify to properly detect changes in arrays/objects
    const initialCustomerVariablesKey = JSON.stringify(
        options?.initialCustomerVariables?.map((v) => v.name) || []
    )
    const initialSharedVariablesKey = JSON.stringify(
        options?.initialSharedVariables?.map((v) => v.name) || []
    )

    useEffect(() => {
        if (
            options?.initialCustomerVariables &&
            options.initialCustomerVariables.length > 0
        ) {
            // Extract names from full variable objects
            setCustomerVariableNames(
                options.initialCustomerVariables.map((v) => v.name)
            )
        }
    }, [initialCustomerVariablesKey])

    useEffect(() => {
        if (
            options?.initialSharedVariables &&
            options.initialSharedVariables.length > 0
        ) {
            // Extract values from full shared variable objects
            const values: Record<string, string> = {}
            options.initialSharedVariables.forEach((v) => {
                if (v.value !== undefined) {
                    values[v.name] = v.value
                }
            })
            setSharedVariableValues(values)
        }
    }, [initialSharedVariablesKey])

    // Reset tracked variables when template changes
    const selectedTemplateVariablesKey = JSON.stringify(
        selectedTemplateVariables.map((v) => v.name)
    )
    useEffect(() => {
        // Clear tracked variables so it falls back to new template variables
        setTrackedVariables([])
    }, [selectedTemplateVariablesKey])

    // Handle variables change from TextEditor
    const handleEditorVariablesChange = useCallback(
        (variables: TemplateVariable[]) => {
            setTrackedVariables(variables)
        },
        []
    )

    // Extract variables used in content
    const usedVariablesInContent = useMemo(() => {
        const content = templateContent || ''
        const matches: string[] = []

        // Match ${name} format
        const dollarRegex = /\$\{([^}]+)\}/g
        let match
        while ((match = dollarRegex.exec(content)) !== null) {
            if (!matches.includes(match[1])) {
                matches.push(match[1])
            }
        }

        // Match chip HTML format
        const chipRegex = /data-name="([^"]+)"/g
        while ((match = chipRegex.exec(content)) !== null) {
            if (!matches.includes(match[1])) {
                matches.push(match[1])
            }
        }

        return matches
    }, [templateContent])

    // Get all available variables (merge template variables with tracked/custom variables)
    const allAvailableVariables = useMemo((): MailerTemplateVariableItem[] => {
        // If we have tracked variables from TextEditor, use them (includes custom)
        if (trackedVariables.length > 0) {
            return trackedVariables.map((v) => ({
                name: v.name,
                label: v.label,
                description: v.description,
            }))
        }
        // Fallback to template variables
        return selectedTemplateVariables
    }, [trackedVariables, selectedTemplateVariables])

    // Get active variables (only those used in content)
    const activeVariables = useMemo(() => {
        return allAvailableVariables.filter((v) =>
            usedVariablesInContent.includes(v.name)
        )
    }, [allAvailableVariables, usedVariablesInContent])

    // Reserved receiver fields - these should always be in receiverVariables, never in sharedVariables
    const RESERVED_RECEIVER_FIELDS = ['email']

    // System variable names - these are handled by backend, not shown in receiver/shared sections
    const SYSTEM_VARIABLE_NAMES = SYSTEM_VARIABLES.map((v) => v.name)

    // Split variables (excluding system variables - they're only for insertion, not for mapping)
    const { receiverVariables, sharedVariables } = useMemo(() => {
        const receiverVars: MailerTemplateVariableItem[] = []
        const sharedVars: MailerTemplateVariableItem[] = []

        // Filter out system variables from activeVariables
        const nonSystemVariables = activeVariables.filter(
            (v) => !SYSTEM_VARIABLE_NAMES.includes(v.name)
        )

        // For alwaysShowReceiverVariables mode (internal filters):
        // - Receiver vars: always show all customerVariableNames (even if not in content)
        // - Shared vars: only show template vars that ARE in content
        if (options?.alwaysShowReceiverVariables) {
            // Add all customer variables to receiver (fixed, always shown)
            options.initialCustomerVariables?.forEach((variable) => {
                receiverVars.push(variable)
            })

            // Add only used variables to shared (filtered by content)
            nonSystemVariables.forEach((variable) => {
                // Skip if it's a customer variable (already in receiver)
                if (
                    RESERVED_RECEIVER_FIELDS.includes(variable.name) ||
                    customerVariableNames.includes(variable.name)
                ) {
                    return
                }
                sharedVars.push(variable)
            })
        } else {
            // Normal mode: split nonSystemVariables based on customerVariableNames
            nonSystemVariables.forEach((variable) => {
                if (
                    RESERVED_RECEIVER_FIELDS.includes(variable.name) ||
                    customerVariableNames.includes(variable.name)
                ) {
                    receiverVars.push(variable)
                } else {
                    sharedVars.push(variable)
                }
            })
        }

        return { receiverVariables: receiverVars, sharedVariables: sharedVars }
    }, [
        activeVariables,
        customerVariableNames,
        options?.alwaysShowReceiverVariables,
        options?.initialCustomerVariables,
    ])

    // Editor template variables (includes system variables)
    const editorTemplateVariables = useMemo(() => {
        const colors = [
            'blue',
            'green',
            'purple',
            'orange',
            'pink',
            'teal',
            'indigo',
            'cyan',
            'red',
            'yellow',
        ] as const

        // User/template variables
        const templateVars = selectedTemplateVariables.map((v, index) => ({
            name: v.name,
            label: v.label,
            description: v.description,
            color: colors[index % 10],
        }))

        // System variables (gray color to distinguish)
        const systemVars = SYSTEM_VARIABLES.map((v) => ({
            name: v.name,
            label: v.label,
            description: v.description,
            color: 'gray' as const,
        }))

        return [...templateVars, ...systemVars]
    }, [selectedTemplateVariables])

    // Variable movement
    const moveToCustomer = (varName: string) => {
        setCustomerVariableNames((prev) => [...prev, varName])
    }

    const moveToShared = (varName: string) => {
        // Don't allow moving reserved fields (like email) to shared
        if (RESERVED_RECEIVER_FIELDS.includes(varName)) return
        setCustomerVariableNames((prev) => prev.filter((n) => n !== varName))
    }

    // Drag handlers
    const handleDragStart = (varName: string) => setDraggedVariable(varName)
    const handleDragOver = (e: React.DragEvent) => e.preventDefault()

    const handleDropToCustomer = (e: React.DragEvent) => {
        e.preventDefault()
        if (
            draggedVariable &&
            !customerVariableNames.includes(draggedVariable)
        ) {
            moveToCustomer(draggedVariable)
        }
        setDraggedVariable(null)
    }

    const handleDropToShared = (e: React.DragEvent) => {
        e.preventDefault()
        // Don't allow moving reserved fields (like email) to shared
        if (
            draggedVariable &&
            customerVariableNames.includes(draggedVariable) &&
            !RESERVED_RECEIVER_FIELDS.includes(draggedVariable)
        ) {
            moveToShared(draggedVariable)
        }
        setDraggedVariable(null)
    }

    const handleSharedValueChange = (varName: string, value: string) => {
        setSharedVariableValues((prev) => ({ ...prev, [varName]: value }))
    }

    // Replace variables in content
    const replaceVariables = (
        content: string,
        receivers: MailerReceiver[],
        receiverIndex: number = 0
    ): string => {
        let result = content
        const receiver = receivers[receiverIndex] || {}

        const replaceVariable = (
            varName: string,
            value: string,
            fallbackLabel: string
        ) => {
            // If has value: blue background (from shared), if no value: green placeholder
            const displayValue = value
                ? `<span style="background-color: #dbeafe; color: #1e40af; padding: 2px 8px; border-radius: 4px; font-size: 0.85em; font-weight: 500;">${value}</span>`
                : `<span style="background-color: #dcfce7; color: #166534; padding: 2px 8px; border-radius: 4px; font-size: 0.85em; font-weight: 500;">[${fallbackLabel}]</span>`

            result = result.replace(
                new RegExp(`\\$\\{${varName}\\}`, 'g'),
                displayValue
            )

            const chipRegex = new RegExp(
                `<span[^>]*data-template-variable[^>]*data-name="${varName}"[^>]*>[^<]*</span>`,
                'g'
            )
            result = result.replace(chipRegex, displayValue)
        }

        receiverVariables.forEach((variable) => {
            replaceVariable(
                variable.name,
                receiver[variable.name] || '',
                variable.label
            )
        })

        if (receiver.email) {
            replaceVariable('email', receiver.email, 'Email')
        }

        sharedVariables.forEach((variable) => {
            replaceVariable(
                variable.name,
                sharedVariableValues[variable.name] || '',
                variable.label
            )
        })

        return result
    }

    // Create full shared variables with values for saving
    const sharedVariablesWithValues =
        useMemo((): MailerCampaignSharedVariable[] => {
            return sharedVariables.map((v) => ({
                name: v.name,
                label: v.label,
                description: v.description,
                value: sharedVariableValues[v.name] || '',
            }))
        }, [sharedVariables, sharedVariableValues])

    return {
        activeVariables,
        allAvailableVariables,
        receiverVariables,
        sharedVariables,
        sharedVariablesWithValues, // Full shared vars with values for saving
        editorTemplateVariables,
        customerVariableNames,
        sharedVariableValues,
        draggedVariable,
        showPreview,
        previewReceiverIndex,
        setShowPreview,
        setPreviewReceiverIndex,
        moveToCustomer,
        moveToShared,
        handleDragStart,
        handleDragOver,
        handleDropToCustomer,
        handleDropToShared,
        handleSharedValueChange,
        handleEditorVariablesChange,
        replaceVariables,
    }
}
